<template>
    <div>
        <div v-if="outOfAttempts && !userHasAuthorizedAttempt" class="columns">
            <div class="column">
                <e-message type="is-info">
                    {{ translate('no_additional_attempts') }}
                </e-message>
            </div>
        </div>

        <div v-if="outOfAttempts && userHasAuthorizedAttempt" class="columns">
            <div class="column">
                <e-tag type="is-warning">
                    {{ translate('submitting_this_assignment_will_use_your_authorized_attempt') }}
                </e-tag>
            </div>
        </div>

        <div v-if="uploadError" class="columns">
            <div class="column">
                <e-message type="is-danger">{{ uploadError }}</e-message>
            </div>
        </div>

        <div v-if="hasRubric" class="columns">
            <div class="column">
                <display-rubric :rubric="{ uuid: hasRubric, namespace: rubricNamespace }" />
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <e-field class="activity-content">
                    <display-content :content="activity.activity_content.content" />
                </e-field>
            </div>
        </div>

        <div v-if="canLaunchTurnitinFromActivity" class="columns">
            <div class="column">
                <e-button type="is-primary" @click="showReviewDrawer = true"> View Turnitin Assignment </e-button>
            </div>
        </div>

        <div v-if="canSubmit">
            <template v-if="isTurnitinSubmission">
                <student-turnitin-assignment-uploader
                    multiple
                    :is-disabled="usedAllAttemptAuthorizations || readOnly || !hasPermissionToSubmit"
                    :read-only="readOnly"
                    :valid-files-updated="validFiles.length"
                    :max-file-size="maxFileSize"
                    :max-files="maxFiles"
                    :allowed-file-types="allowedFileTypes"
                    :lti-resource-link-id="activityContent.lti_resource_link_id"
                    :show-student-turnitin-submission="showStudentTurnitinSubmission"
                />

                <e-message type="is-info" class="mb-4">
                    After uploading your assignment to Turnitin, it may take a few moments for your submission to appear
                    here.
                </e-message>
            </template>

            <template v-else>
                <upload-input
                    ref="uploadInput"
                    multiple
                    :is-disabled="usedAllAttemptAuthorizations || readOnly || !hasPermissionToSubmit"
                    :max-file-size="maxFileSize"
                    :max-files="maxFiles"
                    :allowed-file-types="allowedFileTypes"
                    @update:modelValue="updateFiles"
                    @validation-error="handleValidationError"
                />

                <attachments v-if="readOnly" :media="media" />

                <e-message v-if="showValidation" :type="minFilesNotMet ? 'is-warning' : 'is-success'">
                    {{ minFiles }} {{ translate('file', minFiles) }} {{ translate('required') }}.

                    {{ validFiles.length }} {{ translate('file', validFiles.length) }}
                    {{ translate('uploaded') }}.
                </e-message>

                <e-message
                    v-if="validationMessage"
                    aria-close-label="Close notification"
                    type="is-warning"
                    @close="validationMessage = ''"
                >
                    <div class="has-text-left" v-html="validationMessage" />
                </e-message>

                <div v-if="validFiles.length" class="columns">
                    <div class="column">
                        <attachments
                            :media="validFiles"
                            inline
                            label="My Uploads"
                            class="submission-chip"
                            @delete="deleteFile"
                        />
                    </div>
                </div>
            </template>

            <div>
                <p class="tw:font-semibold tw:text-lg">Comments</p>
                <e-input
                    v-model="comments"
                    :disabled="usedAllAttemptAuthorizations || readOnly || !hasPermissionToSubmit"
                    :rows="4"
                    type="textarea"
                    fluid
                />
            </div>

            <div v-if="isLate && !userHasAuthorizedAttempt" class="columns">
                <div class="column">
                    <late-notice />
                </div>
            </div>

            <div v-if="showValidation && minFilesNotMet && !isTurnitinSubmission" class="columns">
                <div class="column">
                    <e-message
                        aria-close-label="Close notification"
                        type="is-warning"
                        @close="dirty = false"
                    >
                        {{ translate('min_files_not_met', minFileValidation) }}
                    </e-message>
                </div>
            </div>

            <attempt-authorization-notice
                v-if="hasAnyAttemptAuthorizations"
                :attempt-authorizations="userUnusedAuthorizedAttempts"
            />

            <div v-if="isUploading && uploadProgress !== null" class="columns">
                <div class="column">
                    <e-field label="Upload Progress">
                        <e-progress :value="uploadProgress" type="is-primary" size="is-medium" show-value>
                            {{ uploadProgress }}%
                        </e-progress>
                    </e-field>
                    <p class="help">Uploading files... Please don't close this page.</p>
                </div>
            </div>

            <div v-show="!readOnly" class="columns">
                <div class="column">
                    <e-button :disabled="disabled" :loading="isLoading" type="is-primary" @click="submit">
                        {{ translate('complete_activity') }}
                    </e-button>
                </div>
            </div>
        </div>

        <div v-show="false" ref="confirmationDialog">
            {{ translate('you_are_submitting_the_following_information') }}:
            <br />
            <br />
            <p v-if="isTurnitinSubmission">
                <e-icon icon="check" class="is-success" /> File Uploaded in
                <span class="has-text-primary ml-1">Turnitin</span>
            </p>
            <strong>{{ translate('attachments') }}:</strong>
            <br />
            <ul>
                <li v-for="(file, index) in files" :key="index">
                    {{ file.name }}
                </li>
            </ul>
            <br />
            <strong>Comments:</strong>
            <br />
            {{ comments }}
        </div>

        <div v-if="!readOnly" class="columns">
            <div class="column">
                <hr />
            </div>
        </div>

        <div v-if="!readOnly && canViewSubmissions" class="columns">
            <div class="column">
                <submissions :namespace="namespace" @fetch-submissions="toggleSubmissionLoading">
                    <template #content="{ submission }">
                        <div class="columns">
                            <div class="column">
                                <attachments
                                    :media="submission.submittable.media"
                                    :is-turn-it-submission="activityContent.is_turnitin_submission"
                                    :lti-resource-link-id="activityContent.lti_resource_link_id"
                                    :is-turnitin-enabled="isTurnItInIntegrationEnabled"
                                    :show-turnitin-status-messages="false"
                                    :show-turnitin-submission="isTurnitinSubmission"
                                />
                            </div>
                        </div>

                        <div
                            v-if="
                                isTurnitinSubmission &&
                                    (isTurnItInIntegrationEnabled || submission.submittable.media.length === 0)
                            "
                            class="columns"
                        >
                            <div class="column">
                                <e-button expanded class="py-6">
                                    <span
                                        class="is-flex is-align-items-center is-justify-content-center is-flex-wrap-wrap"
                                        :class="{
                                            'is-flex-direction-column-mobile': true,
                                            'is-flex-direction-row-tablet': true,
                                        }"
                                    >
                                        <e-icon icon="check" class="is-success" />

                                        <span class="ml-2 mt-2-mobile">File Uploaded in</span>

                                        <span class="has-text-primary ml-1 mt-2-mobile">Turnitin</span>

                                        <e-button
                                            class="button is-primary ml-2 mt-2-mobile"
                                            @click="showStudentTurnitinSubmission = true"
                                        >
                                            View Assignment
                                        </e-button>
                                    </span>
                                </e-button>

                                <student-turnitin-assignment-uploader
                                    v-if="isTurnitinSubmission"
                                    multiple
                                    :is-disabled="
                                        usedAllAttemptAuthorizations || readOnly || !hasPermissionToSubmit
                                    "
                                    :read-only="readOnly"
                                    :valid-files-updated="validFiles.length"
                                    :max-file-size="maxFileSize"
                                    :max-files="maxFiles"
                                    :allowed-file-types="allowedFileTypes"
                                    :lti-resource-link-id="activityContent.lti_resource_link_id"
                                    :show-student-turnitin-submission="showStudentTurnitinSubmission"
                                    from-student-submitted
                                />
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column">
                                <e-field label="Comments:">
                                    <div v-if="submission.submittable.comments">
                                        {{ submission.submittable.comments }}
                                    </div>

                                    <p v-else class="has-text-subtle">No comment.</p>
                                </e-field>
                            </div>
                        </div>
                    </template>
                </submissions>
            </div>
        </div>

        <turnitin-review-and-grade-drawer
            v-if="canLaunchTurnitinFromActivity"
            v-model:active="showReviewDrawer"
            :lti-resource-link-id="activityContent.lti_resource_link_id"
        />
    </div>
</template>

<script>
import axios from 'axios';
import some from 'lodash/some.js';
import { useI18n } from 'vue-i18n';
import { mapState } from 'vuex';

import Attachments from '@/components/activity-types/course-activity-types/activities/Attachments.vue';
import Submissions from '@/components/activity-types/course-activity-types/activities/Submissions.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import UploadInput from '@/components/elements/UploadInput.vue';
import DisplayRubric from '@/components/rubric/display-rubric/DisplayRubric.vue';
import StudentTurnitinAssignmentUploader from '@/components/turnitin/StudentTurnitinAssignmentUploader.vue';
import TurnitinReviewAndGradeDrawer from '@/components/turnitin/TurnitinReviewAndGradeDrawer.vue';
import AttemptAuthorizationMixin from '@/mixins/AttemptAuthorization.mixin.js';
import CheckFilesMixin from '@/mixins/CheckFiles.mixin.js';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';
import IsLateMixin from '@/mixins/IsLate.mixin.js';
import ActivityStatus from '@/models/ActivityStatus.js';
import ActivitySubmission from '@/models/ActivitySubmission.js';

export default {
    name: 'FileSubmission',

    emits: ['fetch-submissions'],

    components: {
        UploadInput,
        DisplayContent,
        Attachments,
        Submissions,
        DisplayRubric,
        StudentTurnitinAssignmentUploader,
        TurnitinReviewAndGradeDrawer,
    },

    mixins: [IsLateMixin, CourseActivityMixin, CheckFilesMixin, AttemptAuthorizationMixin],

    data: () => ({
        files: [],
        uploadError: null,
        comments: '',
        dirty: false,
        validationMessage: '',
        maxFileSize: 50 * 1024 * 1024, // 50MB in bytes
        showStudentTurnitinSubmission: false,
        allowedFileTypes: window.LoadingState?.AllowedFileSubmissionFileTypes || [],
        uploadProgress: null,
        isUploading: false,
        usesDirectUpload: false,
        showReviewDrawer: false,
    }),

    setup() {
        const { t: translate } = useI18n();

        return { translate };
    },

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),
        ...mapState('Integrations', ['isTurnItInIntegrationEnabled']),

        validFiles() {
            return this.files.filter(file => this.isValidFileSize(file));
        },

        hasInvalidFiles() {
            return this.files.some(file => !this.isValidFileSize(file));
        },

        disabled() {
            const localFileRulesInvalid =
                !this.isTurnitinSubmission &&
                some([
                    this.validFiles.length < this.minFiles,
                    this.maxFiles > 0 && this.validFiles.length > this.maxFiles,
                ]);

            return some([
                localFileRulesInvalid,
                this.outOfAttempts && !this.userHasAuthorizedAttempt,
                this.usedAllAttemptAuthorizations && this.traditionalAttemptsRemaining === 0,
                this.usedAllAttemptAuthorizations,
                this.isLate &&
                    !this.activity[this.settings].allow_submissions_after_due_date &&
                    !this.userHasAuthorizedAttempt,
                !this.hasPermissionToSubmit,
                this.isLoading,
                this.isUploading,
            ]);
        },

        outOfAttempts() {
            if (this.readOnly || this.admin || this.instructor) return false;

            return this.submissions.length + 1 > this.activity[this.settings].max_attempts_allowed;
        },

        maxFiles() {
            return this.activityContent.max_files;
        },

        minFiles() {
            return this.activityContent.min_files;
        },

        isTurnitinSubmission() {
            return (this.activityContent.is_turnitin_submission ?? false) && this.isTurnItInIntegrationEnabled;
        },

        canLaunchTurnitinFromActivity() {
            return this.canViewSubmissions && this.isTurnitinSubmission && this.activityContent.lti_resource_link_id;
        },

        canSubmit() {
            // Wait until existing submissions have been fetched so we don't render
            // the upload UI before we know whether the student has remaining attempts.
            if (this.submissionsLoading) {
                return false;
            }

            // Check for authorized attempts first - they should bypass all other restrictions
            if (this.userHasAuthorizedAttempt) {
                return true;
            }

            if (!this.canSubmitActivity) {
                return false;
            }

            return !some([
                this.activity[this.settings].max_attempts_allowed > 1 && this.userGrades.length,
                this.outOfAttempts,
                this.isLate && !this.activity[this.settings].allow_submissions_after_due_date,
            ]);
        },

        hasRubric() {
            return this.get('rubric_uuid');
        },

        rubricNamespace() {
            return this.namespace.includes('Temp') ? 'TemplateRubrics' : 'Rubrics';
        },

        remainingFiles() {
            if (!this.files) return this.minFiles;
            return this.minFiles - this.validFiles.length;
        },

        minFilesNotMet() {
            return this.validFiles.length < this.minFiles;
        },

        minFileValidation() {
            return {
                file_count: this.remainingFiles,
                additional: this.dirty && this.files.length > 0 ? 'additional' : '',
                files: this.remainingFiles === 1 ? 'file' : 'files',
            };
        },

        showValidation() {
            if (!this.completed) return true;

            return this.dirty && this.userSubmissions.length > 0;
        },
    },

    watch: {
        validFiles: {
            handler(newFiles) {
                // Ensure ActivityStatus model exists
                let activityStatus = ActivityStatus.find(this.activityId);

                // Create it if it doesn't exist
                if (!activityStatus && this.activity) {
                    ActivityStatus.syncFromActivity(this.activity);
                    activityStatus = ActivityStatus.find(this.activityId);
                }

                // Update file count
                if (activityStatus) {
                    activityStatus.updateFileCount(newFiles.length);
                }
            },
            immediate: true,
        },
    },

    created() {
        if (this.readOnly) {
            this.submissionsLoading = false;
            return;
        }

        if (this.hasPermissionToSubmit) {
            this.verifyActivity();
        }
    },

    async mounted() {
        await this.$store.dispatch('Integrations/getTurnitinRelatedInfo');
    },

    methods: {
        // Override the mixin's fetchAuthUserSubmissions so submissionsLoading is flipped
        // once the fetch resolves. The mixin relies on a parent `@fetch-submissions`
        // listener to invoke a callback, but this component is rendered via
        // <router-view><component :is /></router-view> with no such listener, leaving
        // the flag stuck at true.
        async fetchAuthUserSubmissions() {
            try {
                const filters = {
                    activityId: this.activityId,
                    createdBy: this.$store.getters['Auth/userId'],
                };

                if (['survey', 'quiz'].some(type => this.activity.activity_type !== type)) {
                    const data = await this.$store.dispatch('ActivitySubmissions/fetch', filters);
                    await ActivitySubmission.insert({ data });
                }

                this.$emit('fetch-submissions', () => {
                    this.submissionsLoading = false;
                });
            } finally {
                this.submissionsLoading = false;
            }
        },

        isValidFileSize(file) {
            return file.size <= this.maxFileSize;
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 bytes';

            const bytesInKilobyte = 1024;
            const sizeUnits = ['bytes', 'KB', 'MB', 'GB'];

            const unitIndex = Math.floor(Math.log(bytes) / Math.log(bytesInKilobyte));
            const size = (bytes / bytesInKilobyte ** unitIndex).toFixed(2);

            return `${parseFloat(size)} ${sizeUnits[unitIndex]}`;
        },

        handleValidationError(message) {
            this.validationMessage = message;
        },

        updateFiles(files) {
            this.dirty = true;
            this.uploadError = null;
            this.validationMessage = ''; // Clear validation message when new files are selected

            if (!files) {
                return;
            }

            // Convert to array if single file
            const fileArray = Array.isArray(files) ? files : [files];
            const validationMessages = [];

            // Filter out invalid files from new selection
            const newValidFiles = fileArray.filter(file => this.isValidFileSize(file));
            const invalidFiles = fileArray.filter(file => !this.isValidFileSize(file));

            // Add validation messages for invalid files
            if (invalidFiles.length > 0) {
                const errorMessages = invalidFiles.map(
                    file => `The file "${file.name}" is too large (${this.formatFileSize(file.size)}).`
                );
                errorMessages.push(
                    `The maximum file size allowed is ${this.formatFileSize(this.maxFileSize)}. <br/> Please try uploading a smaller file.`
                );
                validationMessages.push(errorMessages.join('</br>'));
            }

            // Get current valid files
            const currentValidFiles = this.files.filter(file => this.isValidFileSize(file));

            // Check max files limit against current valid files + new valid files
            if (this.maxFiles > 0 && currentValidFiles.length + newValidFiles.length > this.maxFiles) {
                validationMessages.push(
                    `You have reached the maximum number of files allowed (${this.maxFiles}). <br/> Please remove some files before adding more.`
                );
            }

            // Check min files requirement against current valid files + new valid files
            if (this.minFiles > 0 && currentValidFiles.length + newValidFiles.length < this.minFiles) {
                validationMessages.push(`You need to upload at least ${this.minFiles} file(s).`);
            }

            // If we have validation messages, show them
            if (validationMessages.length > 0) {
                this.validationMessage = validationMessages.join('<br/><br/>');
                return;
            }

            // If we get here, all validations passed
            // Only add files that aren't already in the array
            const uniqueNewFiles = newValidFiles.filter(
                newFile =>
                    !currentValidFiles.some(
                        existingFile =>
                            existingFile.name === newFile.name &&
                            existingFile.size === newFile.size &&
                            existingFile.lastModified === newFile.lastModified
                    )
            );

            // Append new valid files to existing files
            this.files = [...this.files, ...uniqueNewFiles];
        },

        async submit() {
            if (this.hasInvalidFiles) {
                const invalidFiles = this.files.filter(file => !this.isValidFileSize(file));
                const errorMessages = invalidFiles.map(
                    file => `The file "${file.name}" is too large (${this.formatFileSize(file.size)}).\n`
                );
                errorMessages.push(
                    `The maximum file size allowed is ${this.formatFileSize(this.maxFileSize)}.\nPlease try uploading a smaller file.`
                );
                this.validationMessage = errorMessages.join('<br/>');
                return;
            }

            await this.$nextTick();
            this.uploadError = null;
            this.validationMessage = '';
            const message = this.$refs.confirmationDialog.innerHTML;

            this.$dialog.confirm({
                title: this.translate('confirm_submission'),
                message,
                cancelText: this.translate('cancel'),
                confirmText: this.translate('confirm'),
                type: 'is-success',
                onConfirm: async () => {
                    this.isLoading = true;
                    this.uploadError = null;

                    try {
                        const formattedFiles = await this.processFilesWithFallback();

                        await this.$store.dispatch(`${this.namespace}/completeActivity`, {
                            uuid: this.activity.uuid,
                            course_id: this.$route.params.courseId,
                            activity_id: this.activityId,
                            activity_type: this.get('activity_type'),
                            activity_submission: {
                                comments: this.comments,
                                files: formattedFiles,
                                uses_direct_upload: this.usesDirectUpload,
                            },
                        });

                        this.reset();

                        this.$edverumToast.open('Submission Successful');

                        if (this.outOfAttempts && this.userHasAuthorizedAttempt) {
                            this.userAuthorizedAttempts[0].$update({
                                used_at: this.$moment.utc(this.$databaseFormat),
                            });
                        }

                        await this.$store.dispatch('Activities/fetchOne', this.activityId);

                        this.verifyActivity();
                    } catch (e) {
                        console.error('File submission error:', e);
                        this.uploadError = e?.response?.data?.message ?? this.translate('upload_error');
                    } finally {
                        this.isLoading = false;
                        this.isUploading = false;
                        this.uploadProgress = null;
                    }
                },
            });
        },

        async processFilesWithFallback() {
            try {
                return await this.processViaS3Upload();
            } catch (error) {
                console.warn('S3 upload failed, falling back to base64:', error);

                this.$edverumToast.open({
                    message: 'Using standard upload method...',
                    type: 'is-info',
                    position: 'is-bottom',
                    duration: 3000,
                });

                return this.processViaBase64Upload();
            }
        },

        async processViaS3Upload() {
            this.isUploading = true;
            this.uploadProgress = 0;
            this.usesDirectUpload = true;

            const uploadResults = [];

            for (let i = 0; i < this.files.length; i++) {
                const file = this.files[i];

                // eslint-disable-next-line no-await-in-loop
                const presignedData = await this.generatePresignedUrl(file);
                // eslint-disable-next-line no-await-in-loop
                await this.uploadToS3(presignedData.presigned_url, file, i);

                uploadResults.push({
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    lastModified: file.lastModified,
                    s3_file_path: presignedData.file_path,
                });
            }

            this.isUploading = false;
            this.uploadProgress = 100;

            return uploadResults;
        },

        async processViaBase64Upload() {
            this.usesDirectUpload = false;

            return Promise.all(
                this.files.map(async file => {
                    const base64 = await this.fileToBase64(file);
                    return {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        lastModified: file.lastModified,
                        data: base64,
                    };
                })
            );
        },

        async generatePresignedUrl(file) {
            const response = await axios.post('/api/file-submissions/presigned-url', {
                activity_id: this.activityId,
                file_name: file.name,
                file_size: file.size,
                file_type: file.type,
            });

            if (!response.data.success) {
                throw new Error(response.data.error || 'Failed to generate upload URL');
            }

            return response.data.data;
        },

        uploadToS3(presignedUrl, file, fileIndex) {
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                xhr.upload.addEventListener('progress', e => {
                    if (e.lengthComputable) {
                        const overallProgress = Math.round(
                            ((fileIndex + e.loaded / e.total) / this.files.length) * 100
                        );
                        this.uploadProgress = Math.min(overallProgress, 99);
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(xhr.response);
                    } else {
                        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error('Network error occurred during upload'));
                });

                xhr.addEventListener('abort', () => {
                    reject(new Error('Upload aborted'));
                });

                xhr.open('PUT', presignedUrl);
                xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
                xhr.send(file);
            });
        },

        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    // Return the full data URL format (data:mime/type;base64,...)
                    // Spatie MediaLibrary's addMediaFromBase64 expects this format
                    resolve(reader.result);
                };
                reader.onerror = error => reject(error);
            });
        },

        reset() {
            this.files = [];
            this.comments = '';
            this.dirty = false;
            this.validationMessage = '';
            this.uploadProgress = null;
            this.isUploading = false;
            this.usesDirectUpload = false;
        },

        deleteFile({ index }) {
            this.files.splice(index, 1);
            this.validationMessage = '';

            if (this.maxFiles > 0 && this.validFiles.length > this.maxFiles) {
                this.validationMessage = `You can only upload up to ${this.maxFiles} file(s). <br/> Please remove some files before adding more.`;
            } else if (this.minFiles > 0 && this.validFiles.length < this.minFiles) {
                this.validationMessage = `You need to upload at least ${this.minFiles} file(s).`;
            }
        },
    },
};
</script>
